import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { RadioOptionsWithConfirm, TextareaWithConfirm } from 'shared-components';
import QuestionPage from '../../../templates/question';
import { ConfigField } from '../../../../types';
import { Q } from '../../../../store/student/types';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { ApplicationState } from '../../../../store';

const useData = () => {
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.shortTitle);
  const config = useSelector((state: ApplicationState) => state.config);
  const isPageComplete = useSelector((state: ApplicationState) => state.student.offer.isPIPage4Complete);
  const importData = useSelector((state: ApplicationState) => state.student.importData);

  const {
    HasCriminalConvictions: hasCriminalConvictionsConfig,
    CriminalConvictionsReason: criminalConvictionsReasonConfig
  }: {
    HasCriminalConvictions: ConfigField;
    CriminalConvictionsReason: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.Page4);

  const {
    hasCriminalConvictions = hasCriminalConvictionsConfig?.defaultValue,
    criminalConvictionReason = criminalConvictionsReasonConfig?.defaultValue
  } = useSelector((state: ApplicationState) => state.student.personalInfo);

  const hasCriminalConvictionsVisible = hasCriminalConvictionsConfig?.visible;
  const criminalConvictionsReasonVisible = criminalConvictionsReasonConfig?.visible && hasCriminalConvictions === true;

  const complete =
    (!hasCriminalConvictionsVisible || !hasCriminalConvictionsConfig?.mandatory || hasCriminalConvictions !== '') &&
    (!criminalConvictionsReasonVisible ||
      !criminalConvictionsReasonConfig?.mandatory ||
      criminalConvictionReason !== '');

  return {
    sectionTitle,
    complete,
    currentStep: 4,
    steps: 10,
    hasCriminalConvictions,
    hasCriminalConvictionsVisible,
    hasCriminalConvictionsConfig,
    criminalConvictionReason,
    criminalConvictionsReasonVisible,
    criminalConvictionsReasonConfig,
    config,
    isPageComplete,
    importData
  };
};

const PersonalQPage4 = () => {
  const {
    sectionTitle,
    complete,
    currentStep,
    steps,
    hasCriminalConvictions,
    hasCriminalConvictionsVisible,
    hasCriminalConvictionsConfig,
    criminalConvictionReason,
    criminalConvictionsReasonVisible,
    criminalConvictionsReasonConfig,
    config,
    isPageComplete,
    importData
  } = useData();

  const getImportApprovedFields = useCallback((): Array<Q> => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData?.criminalConvictions !== undefined && importData?.criminalConvictions === hasCriminalConvictions) {
        importApproved.push(Q.HAS_CRIMINAL_CONVICTIONS);
      }
    }
    return importApproved;
  }, [isPageComplete, importData, hasCriminalConvictions]);

  const getImportUpdateFields = useCallback((): Array<Q> => {
    const importUpdateEnabled = [];
    if (isPageComplete) {
      if (importData?.criminalConvictions !== undefined && importData?.criminalConvictions !== hasCriminalConvictions) {
        importUpdateEnabled.push(Q.HAS_CRIMINAL_CONVICTIONS);
      }
    }
    return importUpdateEnabled;
  }, [isPageComplete, importData, hasCriminalConvictions]);

  const [importApproved, setImportApproved] = useState<Array<Q>>(getImportApprovedFields());
  const [importEdited, setImportEdited] = useState<Array<Q>>([]);

  const dispatch = useDispatch();

  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.savePersonalInfo(Pages.PAGE4, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value) => dispatch(PIActions.createUpdatePIFieldAction(fieldName, value));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const handleConvictionsClick = question => {
    onChange(Q.HAS_CRIMINAL_CONVICTIONS, question.char === 'Y');
    if (question.char === 'N') {
      onChange(Q.CRIMINAL_CONVICTIONS_REASON, '');
    }
  };

  const handleConvictionsInfoChange = (evt: React.FormEvent<HTMLInputElement>) => {
    onChange(Q.CRIMINAL_CONVICTIONS_REASON, evt.currentTarget.value);
  };

  const isImportEnabled = useCallback(
    (target: Q): boolean => {
      return (
        target === Q.HAS_CRIMINAL_CONVICTIONS &&
        hasCriminalConvictionsVisible &&
        importData?.criminalConvictions !== undefined
      );
    },
    [hasCriminalConvictionsVisible, importData?.criminalConvictions]
  );

  const isImportUpdateEnabled = useCallback(
    (target: Q): boolean => {
      if (!isPageComplete || !complete) return false;
      const importUpdateEnabled: Array<Q> = getImportUpdateFields();
      return (
        target === Q.HAS_CRIMINAL_CONVICTIONS &&
        hasCriminalConvictions !== undefined &&
        importUpdateEnabled.includes(target)
      );
    },
    [complete, getImportUpdateFields, hasCriminalConvictions, isPageComplete]
  );

  const isImportComplete = useCallback(
    (target: Q): boolean => {
      return (
        !isImportEnabled(target) ||
        isImportUpdateEnabled(target) ||
        importApproved.includes(target) ||
        importEdited.includes(target)
      );
    },
    [isImportEnabled, isImportUpdateEnabled, importApproved, importEdited]
  );

  const getComplete = () => {
    const importQuestions = [Q.HAS_CRIMINAL_CONVICTIONS];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  };

  const handleImportApprove = (target: Q): void => {
    setImportApproved(prevImportApproved => [...prevImportApproved, target]);
    setImportEdited(prevImportEdited => prevImportEdited.filter(q => q !== target));
  };

  const handleImportEdit = (target: Q): void => {
    setImportApproved(prevImportApproved => prevImportApproved.filter(q => q !== target));
    setImportEdited(prevImportEdited => [...prevImportEdited, target]);
  };

  const handleImportUndo = (target: Q): void => {
    setImportApproved(prevImportApproved => prevImportApproved.filter(q => q !== target));
    setImportEdited(prevImportEdited => prevImportEdited.filter(q => q !== target));
  };

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {hasCriminalConvictionsVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_CRIMINAL_CONVICTIONS}
          id={Q.HAS_CRIMINAL_CONVICTIONS}
          value={hasCriminalConvictions !== undefined ? (hasCriminalConvictions ? 'Yes' : 'No') : undefined}
          label={hasCriminalConvictionsConfig?.label}
          inputHelperText={hasCriminalConvictionsConfig?.helpText}
          onClick={question => handleConvictionsClick(question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasCriminalConvictions === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasCriminalConvictions === false
            }
          ]}
          importEnabled={isImportEnabled(Q.HAS_CRIMINAL_CONVICTIONS)}
          importValue={
            importData?.criminalConvictions !== undefined ? (importData?.criminalConvictions ? 'Yes' : 'No') : undefined
          }
          importUpdateEnabled={isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS)}
          importApproved={importApproved.includes(Q.HAS_CRIMINAL_CONVICTIONS)}
          importEdited={importEdited.includes(Q.HAS_CRIMINAL_CONVICTIONS)}
          onImportApprove={() => {
            handleImportApprove(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(Q.HAS_CRIMINAL_CONVICTIONS, importData?.criminalConvictions);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(
              Q.HAS_CRIMINAL_CONVICTIONS,
              isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS)
                ? hasCriminalConvictions
                : importData?.criminalConvictions
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(
              Q.HAS_CRIMINAL_CONVICTIONS,
              isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS) ? hasCriminalConvictions : undefined
            );
          }}
        />
      )}

      {criminalConvictionsReasonVisible && (
        <TextareaWithConfirm
          id={Q.CRIMINAL_CONVICTIONS_REASON}
          value={criminalConvictionReason}
          label={criminalConvictionsReasonConfig?.label}
          inputHelperText={criminalConvictionsReasonConfig?.helpText}
          onChange={handleConvictionsInfoChange}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage4;
